import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "pxos.token";
const REFRESH_KEY = "pxos.refresh";
const CSRF_KEY = "pxos.csrf";

export function getToken() { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } }
export function setToken(t) { try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch {} }
export function setRefresh(t) { try { if (t) localStorage.setItem(REFRESH_KEY, t); else localStorage.removeItem(REFRESH_KEY); } catch {} }
export function setCsrf(t) { try { if (t) localStorage.setItem(CSRF_KEY, t); else localStorage.removeItem(CSRF_KEY); } catch {} }
export function getCsrf() { try { return localStorage.getItem(CSRF_KEY); } catch { return null; } }
export function clearSession() { setToken(null); setRefresh(null); setCsrf(null); }

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  const csrf = getCsrf();
  if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
  const stepup = window.__pxos_stepup;
  if (stepup) { cfg.headers["X-Stepup-Token"] = stepup; window.__pxos_stepup = null; }
  return cfg;
});

// ─── Refresh trasparente + retry robusto ─────────────────────
// Se access token scade (401) → tentiamo /auth/refresh una volta e ripetiamo la richiesta.
// Su errori di rete o 5xx → NON facciamo logout, lasciamo che il chiamante gestisca.
let _refreshInFlight = null;

async function tryRefresh() {
  if (!_refreshInFlight) {
    _refreshInFlight = (async () => {
      try {
        const r = await axios.post(`${API}/auth/refresh`, {}, {
          withCredentials: true,
          headers: { "X-CSRF-Token": getCsrf() || "" },
        });
        if (r.data?.token) setToken(r.data.token);
        if (r.data?.refresh) setRefresh(r.data.refresh);
        if (r.data?.csrf) setCsrf(r.data.csrf);
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => { _refreshInFlight = null; }, 0);
      }
    })();
  }
  return _refreshInFlight;
}

const AUTH_ALLOW_PATHS = ["/login", "/register", "/verify-email", "/password-reset", "/invitations"];

client.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err?.config || {};
    const status = err?.response?.status;
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const publicPage = AUTH_ALLOW_PATHS.some((p) => path.startsWith(p));

    // 401: prova refresh trasparente una sola volta, poi retry.
    if (status === 401 && !cfg.__isRetry && !publicPage) {
      const url = cfg.url || "";
      // NON considerare 401 di sessione:
      //  - endpoint di refresh/login stesso (evita cicli)
      //  - endpoint stepup password (password errata → NON è sessione scaduta)
      //  - confirm di azione chatbot con detail=stepup_required (serve solo prompt)
      const detail = err?.response?.data?.detail;
      const isStepupWrongPwd = url.includes("/auth/stepup/");
      const isStepupRequired = url.includes("/chatbot/actions/") && url.endsWith("/confirm") &&
                                detail === "stepup_required";
      if (isStepupWrongPwd || isStepupRequired) {
        return Promise.reject(err);
      }
      // Skip refresh se l'endpoint stesso è /auth/refresh o /auth/login (per non ciclare)
      if (!url.includes("/auth/refresh") && !url.includes("/auth/login")) {
        const ok = await tryRefresh();
        if (ok) {
          cfg.__isRetry = true;
          const tok = getToken();
          if (tok) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${tok}` };
          try {
            return await client.request(cfg);
          } catch (e2) {
            // Retry fallito: NON facciamo logout automatico se lo status non è 401 di sessione
            // (es. 429 rate limit, 401 su credenziali sbagliate ecc.)
            const e2s = e2?.response?.status;
            if (e2s !== 401) return Promise.reject(e2);
            err = e2;
          }
        }
      }
      // Refresh fallito o non applicabile → logout
      clearSession();
      if (typeof window !== "undefined") {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?redirect=${next}`;
      }
      return Promise.reject(err);
    }

    // Errori di rete o 5xx: retry con backoff (max 2 tentativi) senza logout.
    const isNetworkErr = !err?.response;
    const is5xx = status && status >= 500;
    if ((isNetworkErr || is5xx) && !cfg.__retryCount) cfg.__retryCount = 0;
    if ((isNetworkErr || is5xx) && cfg.__retryCount < 2) {
      cfg.__retryCount++;
      const wait = 500 * Math.pow(2, cfg.__retryCount - 1);
      await new Promise((r) => setTimeout(r, wait));
      try { return await client.request(cfg); } catch (e2) { return Promise.reject(e2); }
    }

    return Promise.reject(err);
  }
);

function saveSession(res) {
  if (res?.token) setToken(res.token);
  if (res?.refresh) setRefresh(res.refresh);
  if (res?.csrf) setCsrf(res.csrf);
}

export const api = {
  publicConfig: () => client.get("/config/public").then((r) => r.data),

  register: (payload) => client.post("/auth/register", payload).then((r) => r.data),
  verifyEmail: (email, otp) => client.post("/auth/verify-email", { email, otp }).then((r) => r.data),
  resendOtp: (email, purpose = "register_verify") => client.post("/auth/resend-otp", { email, purpose }).then((r) => r.data),

  login: (email, password, remember = false) => client.post("/auth/login", { email, password, remember_device: remember }).then((r) => { saveSession(r.data); return r.data; }),
  loginOtpRequest: (email) => client.post("/auth/login-otp/request", { email }).then((r) => r.data),
  loginOtpVerify: (email, otp) => client.post("/auth/login-otp/verify", { email, otp }).then((r) => { saveSession(r.data); return r.data; }),
  twoFAVerify: (temp_token, totp_code, recovery_code) => client.post("/auth/2fa/verify", { temp_token, totp_code, recovery_code }).then((r) => { saveSession(r.data); return r.data; }),

  refresh: () => client.post("/auth/refresh").then((r) => { saveSession(r.data); return r.data; }),
  logout: () => client.post("/auth/logout").then((r) => { clearSession(); return r.data; }),

  passwordResetRequest: (email) => client.post("/auth/password-reset/request", { email }).then((r) => r.data),
  passwordResetValidate: (token) => client.get(`/auth/password-reset/validate?token=${encodeURIComponent(token)}`).then((r) => r.data),
  passwordResetConfirm: (token, new_password, new_password_confirm) => client.post("/auth/password-reset/confirm", { token, new_password, new_password_confirm }).then((r) => r.data),
  changePassword: (current_password, new_password, new_password_confirm) => client.post("/auth/change-password", { current_password, new_password, new_password_confirm }).then((r) => r.data),

  me: () => client.get("/auth/me").then((r) => r.data),

  stepupPassword: (password) => client.post("/auth/stepup/password", { password }).then((r) => r.data),
  stepupOtpRequest: () => client.post("/auth/stepup/otp/request").then((r) => r.data),
  stepupOtpVerify: (otp) => client.post("/auth/stepup/otp/verify", { otp }).then((r) => r.data),

  twoFASetup: () => client.post("/auth/2fa/setup").then((r) => r.data),
  twoFAEnable: (code) => client.post("/auth/2fa/enable", { code }).then((r) => r.data),
  twoFADisable: () => client.post("/auth/2fa/disable").then((r) => r.data),
  twoFARegenerate: () => client.post("/auth/2fa/recovery-codes/regenerate").then((r) => r.data),

  sessions: () => client.get("/sessions").then((r) => r.data),
  revokeSession: (id) => client.post(`/sessions/${id}/revoke`).then((r) => r.data),
  revokeOthers: () => client.post("/sessions/revoke-others").then((r) => r.data),
  revokeAll: () => client.post("/sessions/revoke-all").then((r) => r.data),
  trustSession: (id) => client.post(`/sessions/${id}/trust`).then((r) => r.data),
  untrustSession: (id) => client.post(`/sessions/${id}/untrust`).then((r) => r.data),

  invitations: () => client.get("/invitations").then((r) => r.data),
  createInvitation: (p) => client.post("/invitations", p).then((r) => r.data),
  revokeInvitation: (id) => client.post(`/invitations/${id}/revoke`).then((r) => r.data),

  listUsers: () => client.get("/users").then((r) => r.data),
  suspendUser: (id) => client.post(`/users/${id}/suspend`).then((r) => r.data),
  unsuspendUser: (id) => client.post(`/users/${id}/unsuspend`).then((r) => r.data),
  deleteUserAccess: (id) => client.post(`/users/${id}/delete-access`).then((r) => r.data),

  auditLogs: (limit = 200) => client.get(`/audit-logs?limit=${limit}`).then((r) => r.data),

  // ─── Phase 0 · Documents ───────────────────────────────────
  documents: () => client.get("/documents").then((r) => r.data),
  listDocuments: () => client.get("/documents").then((r) => r.data),
  uploadDocuments: (files) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    return client.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  documentExtraction: (id) => client.get(`/documents/${id}/extraction`).then((r) => r.data),
  extraction: (id) => client.get(`/documents/${id}/extraction`).then((r) => r.data),
  signedUrl: (id) => client.get(`/documents/${id}/signed-url`).then((r) => r.data),
  editField: (extractionId, fieldId, payload) => client.patch(`/extractions/${extractionId}/fields/${fieldId}`, payload).then((r) => r.data),
  confirmExtraction: (id) => client.post(`/extractions/${id}/confirm`).then((r) => r.data),
  rejectExtraction: (id) => client.post(`/extractions/${id}/reject`).then((r) => r.data),
  deleteDocument: (id) => client.delete(`/documents/${id}`).then((r) => r.data),

  // ─── Invitations lookup/accept (public) ────────────────────
  invitationLookup: (token) => client.get(`/invitations/lookup?token=${encodeURIComponent(token)}`).then((r) => r.data),
  acceptInvitation: (payload) => client.post("/invitations/accept", payload).then((r) => r.data),

  // ─── Phase 2 · Profile ─────────────────────────────────────
  getProfile: () => client.get("/profile").then((r) => r.data),
  updateProfile: (p) => client.patch("/profile", p).then((r) => r.data),

  // ─── Fase 2 · End-of-day reminder ──────────────────────────
  getEndOfDayPrefs: () => client.get("/user/preferences/end-of-day").then((r) => r.data),
  updateEndOfDayPrefs: (p) => client.patch("/user/preferences/end-of-day", p).then((r) => r.data),
  endOfDayConfirmAll: (payload = {}) =>
    client.post("/tasks/end-of-day/confirm-all", payload).then((r) => r.data),
  endOfDayPostponeAll: (payload = {}) =>
    client.post("/tasks/end-of-day/postpone-all", payload).then((r) => r.data),

  // ─── Phase 2 · Life Areas ──────────────────────────────────
  listAreas: (opts = {}) => client.get("/life-areas", { signal: opts.signal }).then((r) => r.data),
  createArea: (p) => client.post("/life-areas", p).then((r) => r.data),
  updateArea: (id, p) => client.patch(`/life-areas/${id}`, p).then((r) => r.data),
  deleteArea: (id) => client.delete(`/life-areas/${id}`).then((r) => r.data),
  createSubArea: (p) => client.post("/sub-areas", p).then((r) => r.data),
  updateSubArea: (id, p) => client.patch(`/sub-areas/${id}`, p).then((r) => r.data),
  deleteSubArea: (id) => client.delete(`/sub-areas/${id}`).then((r) => r.data),

  // ─── Phase 2 · Projects ────────────────────────────────────
  listProjects: (params = {}) => client.get("/projects", { params }).then((r) => r.data),
  createProject: (p) => client.post("/projects", p).then((r) => r.data),
  updateProject: (id, p) => client.patch(`/projects/${id}`, p).then((r) => r.data),
  deleteProject: (id) => client.delete(`/projects/${id}`).then((r) => r.data),

  // ─── Phase 2 · Tasks ───────────────────────────────────────
  listTasks: (params = {}) => client.get("/tasks", { params }).then((r) => r.data),
  createTask: (p) => client.post("/tasks", p).then((r) => r.data),
  getTask: (id) => client.get(`/tasks/${id}`).then((r) => r.data),
  updateTask: (id, p) => client.patch(`/tasks/${id}`, p).then((r) => r.data),
  scheduleTask: (id, scheduled_date) => client.post(`/tasks/${id}/schedule`, null, { params: { scheduled_date } }).then((r) => r.data),
  completeTask: (id) => client.post(`/tasks/${id}/complete`).then((r) => r.data),
  reopenTask: (id) => client.post(`/tasks/${id}/reopen`).then((r) => r.data),
  archiveTask: (id) => client.post(`/tasks/${id}/archive`).then((r) => r.data),
  unarchiveTask: (id) => client.post(`/tasks/${id}/unarchive`).then((r) => r.data),
  trashTask: (id) => client.delete(`/tasks/${id}`).then((r) => r.data),
  restoreTask: (id) => client.post(`/tasks/${id}/restore`).then((r) => r.data),
  listTrash: () => client.get("/trash/tasks").then((r) => r.data),
  taskReminders: (id) => client.get(`/tasks/${id}/reminders`).then((r) => r.data),
  duplicateTask: (id) => client.post(`/tasks/${id}/duplicate`).then((r) => r.data),

  // ─── Phase 2 · Agenda · Deadlines · Search ─────────────────
  agenda: (view = "day", ref = null, include_completed = false, opts = {}) =>
    client.get("/agenda", {
      params: { view, ref, include_completed, area_id: opts.area_id },
      signal: opts.signal,
    }).then((r) => r.data),
  yearHeatmap: (year, opts = {}) =>
    client.get("/agenda/year-heatmap", { params: { year }, signal: opts.signal }).then((r) => r.data),
  deadlines: (params = {}) => client.get("/deadlines", { params: { group_by: "none", ...params } }).then((r) => r.data),
  search: (q) => client.get("/search", { params: { q } }).then((r) => r.data),
  taskHistory: (params = {}) => client.get("/task-history", { params }).then((r) => r.data),
  taskSearchAdvanced: (params = {}) => client.get("/task-search", { params }).then((r) => r.data),

  // ─── Phase 2 · Saved Views ─────────────────────────────────
  savedViews: (scope) => client.get("/saved-views", { params: { scope } }).then((r) => r.data),
  createSavedView: (p) => client.post("/saved-views", p).then((r) => r.data),
  deleteSavedView: (id) => client.delete(`/saved-views/${id}`).then((r) => r.data),

  // ─── Phase 2 · PDF Reports ─────────────────────────────────
  reportDailyPdfUrl: (day) => `${API}/reports/agenda/daily${day ? `?day=${day}` : ""}`,
  reportWeeklyPdfUrl: (monday) => `${API}/reports/agenda/weekly${monday ? `?monday=${monday}` : ""}`,
  reportTasksPdfUrl: () => `${API}/reports/tasks`,
  reportDeadlinesPdfUrl: () => `${API}/reports/deadlines`,
  // ─── Chatbot ────────────────────────────────────────────
  chatbotListConversations: () => client.get("/chatbot/conversations").then((r) => r.data),
  chatbotCreateConversation: () => client.post("/chatbot/conversations").then((r) => r.data),
  chatbotListMessages: (convId) => client.get(`/chatbot/conversations/${convId}/messages`).then((r) => r.data),
  chatbotSendMessage: (convId, content) => client.post(`/chatbot/conversations/${convId}/messages`, { content }).then((r) => r.data),
  chatbotConfirmAction: (id, stepupToken = null) => {
    const headers = stepupToken ? { "x-stepup-token": stepupToken } : {};
    return client.post(`/chatbot/actions/${id}/confirm`, {}, { headers }).then((r) => r.data);
  },
  chatbotRejectAction: (id) => client.post(`/chatbot/actions/${id}/reject`).then((r) => r.data),
  chatbotUndoAction: (id) => client.post(`/chatbot/actions/${id}/undo`).then((r) => r.data),
  chatbotListActions: (params = {}) => client.get("/chatbot/actions", { params }).then((r) => r.data),

  // ─── Segretaria IA · Memoria ──────────────────────────
  memoriesList: () => client.get("/memories").then((r) => r.data),
  memoriesCreate: (payload) => client.post("/memories", payload).then((r) => r.data),
  memoriesUpdate: (id, payload) => client.patch(`/memories/${id}`, payload).then((r) => r.data),
  memoriesDelete: (id) => client.delete(`/memories/${id}`).then((r) => r.data),

  // ─── Personal goals (immutable) — Livello 1 "Mete" (1/5/10 anni)
  listGoals: (params = {}) => client.get("/personal-goals", { params }).then((r) => r.data),
  listGoalsWithProgress: (params = {}) => client.get("/personal-goals-with-progress", { params }).then((r) => r.data),
  getGoal: (id) => client.get(`/personal-goals/${id}`).then((r) => r.data),
  getGoalProgress: (id) => client.get(`/personal-goals/${id}/progress`).then((r) => r.data),
  listGoalAzioni: (id) => client.get(`/personal-goals/${id}/azioni`).then((r) => r.data),
  createGoal: (payload) => client.post("/personal-goals", payload).then((r) => r.data),
  markGoalAchieved: (id, achieved_notes = null) => client.post(`/personal-goals/${id}/mark-achieved`, { achieved_notes }).then((r) => r.data),
  markGoalAbandoned: (id, reason) => client.post(`/personal-goals/${id}/mark-abandoned`, { reason }).then((r) => r.data),
  reactivateGoal: (id) => client.post(`/personal-goals/${id}/reactivate`).then((r) => r.data),
  updateGoalNotes: (id, payload) => client.post(`/personal-goals/${id}/update-notes`, payload).then((r) => r.data),
  deleteGoal: (id) => client.delete(`/personal-goals/${id}`).then((r) => r.data),
  // Blocco 4 · F · Lifecycle Piera-style
  achieveGoal: (id, { achieved_date = null, achieved_notes = null } = {}) =>
    client.post(`/personal-goals/${id}/achieve`, { achieved_date, achieved_notes }).then((r) => r.data),
  cancelGoal: (id, reason = null) =>
    client.post(`/personal-goals/${id}/cancel`, { reason }).then((r) => r.data),
  restoreGoal: (id) => client.post(`/personal-goals/${id}/restore`).then((r) => r.data),
  patchGoal: (id, payload) => client.patch(`/personal-goals/${id}`, payload).then((r) => r.data),
  achieveMeta: (id, { achieved_date = null } = {}) =>
    client.post(`/mete/${id}/achieve`, { achieved_date }).then((r) => r.data),
  cancelMeta: (id, reason = null) =>
    client.post(`/mete/${id}/cancel`, { reason }).then((r) => r.data),
  restoreMeta: (id) => client.post(`/mete/${id}/restore`).then((r) => r.data),

  // ─── Azioni (Fase 2b · Livello 2 · gerarchia annuale/mensile/settimanale) ─
  // Backend endpoint: /api/mete/* (nome DB non cambia, UI lo chiama "Azioni")
  listMete: (params = {}) => client.get("/mete", { params }).then((r) => r.data),
  getMeteTree: () => client.get("/mete/tree").then((r) => r.data),
  getMeta: (id) => client.get(`/mete/${id}`).then((r) => r.data),
  createMeta: (payload) => client.post("/mete", payload).then((r) => r.data),
  updateMeta: (id, payload) => client.patch(`/mete/${id}`, payload).then((r) => r.data),
  deleteMeta: (id) => client.delete(`/mete/${id}`).then((r) => r.data),
  linkTaskToMeta: (metaId, task_id, weight = 1.0) =>
    client.post(`/mete/${metaId}/link-task`, { task_id, weight }).then((r) => r.data),
  unlinkTaskFromMeta: (metaId, taskId) =>
    client.delete(`/mete/${metaId}/link-task/${taskId}`).then((r) => r.data),
  getMetaProgress: (id) => client.get(`/mete/${id}/progress`).then((r) => r.data),
  getTaskMete: (taskId) => client.get(`/tasks/${taskId}/mete`).then((r) => r.data),

  // ─── Momentum + digest v2 prefs (Fase 2b² · Blocco 2) ──
  getMomentumWeekly: () => client.get("/momentum/weekly").then((r) => r.data),
  getTomorrowRecapPrefs: () => client.get("/user/preferences/tomorrow-recap").then((r) => r.data),
  updateTomorrowRecapPrefs: (payload) => client.patch("/user/preferences/tomorrow-recap", payload).then((r) => r.data),
  getWeeklyDigestPrefs: () => client.get("/user/preferences/weekly-digest").then((r) => r.data),
  updateWeeklyDigestPrefs: (payload) => client.patch("/user/preferences/weekly-digest", payload).then((r) => r.data),

  // ─── Security activity (P4) ─────────────────────────────
  securityActivity: () => client.get("/security/activity").then((r) => r.data),
  reportSuspicious: (description) => client.post("/security/report-suspicious", { description }).then((r) => r.data),

  // ─── Mobile badges ──────────────────────────────────────
  mobileBadges: () => client.get("/mobile/badges").then((r) => r.data),

  // ─── Recurrence NL interpret ────────────────────────────
  recurrenceInterpret: (natural_language, base_date, context = null) =>
    client.post("/recurrence/interpret", { natural_language, base_date, context }).then((r) => r.data),

  // ─── Finances (Phase 4) ─────────────────────────────────
  financesDashboard: (ref_date = null) => client.get("/finances/dashboard", { params: ref_date ? { ref_date } : {} }).then((r) => r.data),
  financesSeedTemplates: () => client.post("/finances/seed-templates").then((r) => r.data),
  financesListItems: (params = {}) => client.get("/finances/items", { params }).then((r) => r.data),
  financesGetItem: (id) => client.get(`/finances/items/${id}`).then((r) => r.data),
  financesCreateItem: (payload) => client.post("/finances/items", payload).then((r) => r.data),
  financesUpdateItem: (id, payload) => client.patch(`/finances/items/${id}`, payload).then((r) => r.data),
  financesDeleteItem: (id) => client.delete(`/finances/items/${id}`).then((r) => r.data),
  financesMarkPaid: (id, payload) => client.post(`/finances/items/${id}/mark-paid`, payload).then((r) => r.data),
  financesMarkCancelled: (id) => client.post(`/finances/items/${id}/mark-cancelled`).then((r) => r.data),
  financesListAccounts: () => client.get("/finances/accounts").then((r) => r.data),
  financesCreateAccount: (payload) => client.post("/finances/accounts", payload).then((r) => r.data),
  financesUpdateAccount: (id, payload) => client.patch(`/finances/accounts/${id}`, payload).then((r) => r.data),
  financesDeleteAccount: (id) => client.delete(`/finances/accounts/${id}`).then((r) => r.data),
  financesGetSettings: () => client.get("/finances/settings").then((r) => r.data),
  financesUpdateSettings: (payload) => client.patch("/finances/settings", payload).then((r) => r.data),
  financesCashflow: (months = 12) => client.get("/finances/cashflow", { params: { months } }).then((r) => r.data),
  financesAiSuggest: () => client.post("/finances/ai-suggest").then((r) => r.data),

  // ─── Google Calendar (integrations) ──────────────────────
  googleStatus: () => client.get("/integrations/google/status").then((r) => r.data),
  googleCalendars: () => client.get("/integrations/google/calendars").then((r) => r.data),
  googleSyncNow: () => client.post("/integrations/google/sync-now").then((r) => r.data),
  googleSyncStatus: () => client.get("/integrations/google/sync-status").then((r) => r.data),
  googleRetrySync: (taskId) => client.post(`/integrations/google/retry-sync/${taskId}`).then((r) => r.data),

  // ─── Documents (Fase 5) ──────────────────────────────
  docsList: (params = {}) => client.get("/documents", { params }).then((r) => r.data),
  docsStats: () => client.get("/documents/stats").then((r) => r.data),
  docsTrash: () => client.get("/documents/trash").then((r) => r.data),
  docsSearch: (q, limit = 20) => client.get("/documents/search", { params: { q, limit } }).then((r) => r.data),
  docsGet: (id) => client.get(`/documents/${id}`).then((r) => r.data),
  docsPatch: (id, payload) => client.patch(`/documents/${id}`, payload).then((r) => r.data),
  docsDelete: (id) => client.delete(`/documents/${id}`).then((r) => r.data),
  docsRestore: (id) => client.post(`/documents/${id}/restore`).then((r) => r.data),
  docsReprocessAI: (id) => client.post(`/documents/${id}/reprocess-ai`).then((r) => r.data),
  docsAccessLogs: (id, limit = 30) => client.get(`/documents/${id}/access-logs`, { params: { limit } }).then((r) => r.data),
  docsUpload: (files, extras = {}) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    const params = new URLSearchParams();
    Object.entries(extras).forEach(([k, v]) => { if (v) params.append(k, v); });
    const qs = params.toString();
    return client.post(`/documents/upload${qs ? "?" + qs : ""}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },

  // ─── System status (public · no auth) ──────────────
  systemStatus: () => client.get("/system/status").then((r) => r.data),

  // ─── Task 3 · Fatture Commercialista ────────────────
  accountantGetSettings: () => client.get("/accountant/settings").then((r) => r.data),
  accountantPatchSettings: (p) => client.patch("/accountant/settings", p).then((r) => r.data),
  accountantInbox: (fiscalYear = null) =>
    client.get("/accountant/inbox", { params: fiscalYear ? { fiscal_year: fiscalYear } : {} }).then((r) => r.data),
  accountantHistory: (limit = 50) =>
    client.get("/accountant/history", { params: { limit } }).then((r) => r.data),
  /**
   * Send a batch. `stepupToken` is a short-lived L2 token obtained by
   * POST /auth/stepup/password — the backend REQUIRES this header;
   * calling without it yields 401 "Step-up richiesto".
   */
  accountantSend: (payload, stepupToken) =>
    client.post("/accountant/send", payload, {
      headers: { "x-stepup-token": stepupToken },
    }).then((r) => r.data),

  // ─── Task A · Digest notifications ─────────────────────
  digestGetSettings: () => client.get("/digest/settings").then((r) => r.data),
  digestUpdateSettings: (payload) => client.put("/digest/settings", payload).then((r) => r.data),
  digestRunNow: (kind) => client.post(`/digest/${kind}/run-now`).then((r) => r.data),
  digestLogs: (params = {}) => client.get("/digest/logs", { params }).then((r) => r.data),
  digestPreviewUrl: (kind) => `${API}/digest/preview/${kind}`,

  // ─── Task B · Notifications (in-app bell) ─────────────
  notifUnreadCount: () => client.get("/notifications/unread-count").then((r) => r.data),
  notifList: (params = {}) => client.get("/notifications", { params }).then((r) => r.data),
  notifRead: (id) => client.post(`/notifications/${id}/read`).then((r) => r.data),
  notifReadAll: () => client.post("/notifications/read-all").then((r) => r.data),
  notifDelete: (id) => client.delete(`/notifications/${id}`).then((r) => r.data),

  // ─── Task B · CRM Prime Vision ───────────────────────
  crmStages: () => client.get("/crm/stages").then((r) => r.data),
  crmStagesReorder: (order) => client.post("/crm/stages/reorder", { order }).then((r) => r.data),
  crmStageCreate: (payload) => client.post("/crm/stages", payload).then((r) => r.data),
  crmStagePatch: (id, payload) => client.patch(`/crm/stages/${id}`, payload).then((r) => r.data),
  crmLeads: (params = {}) => client.get("/crm/leads", { params }).then((r) => r.data),
  crmLead: (id) => client.get(`/crm/leads/${id}`).then((r) => r.data),
  crmLeadCreate: (payload) => client.post("/crm/leads", payload).then((r) => r.data),
  crmLeadPatch: (id, payload) => client.patch(`/crm/leads/${id}`, payload).then((r) => r.data),
  crmLeadDelete: (id) => client.delete(`/crm/leads/${id}`).then((r) => r.data),
  crmMoveStage: (id, payload) => client.post(`/crm/leads/${id}/move-stage`, payload).then((r) => r.data),
  crmLogActivity: (leadId, payload) => client.post(`/crm/leads/${leadId}/log-activity`, payload).then((r) => r.data),
  crmActivities: (params = {}) => client.get("/crm/activities", { params }).then((r) => r.data),
  crmActivityPatch: (id, payload) => client.patch(`/crm/activities/${id}`, payload).then((r) => r.data),
  crmAlerts: () => client.get("/crm/alerts").then((r) => r.data),
  crmStats: () => client.get("/crm/stats").then((r) => r.data),

  // ─── Task B · Formazione ECM/Congressi ───────────────
  formazioneList: (params = {}) => client.get("/formazione/items", { params }).then((r) => r.data),
  formazioneCreate: (payload) => client.post("/formazione/items", payload).then((r) => r.data),
  formazionePatch: (id, payload) => client.patch(`/formazione/items/${id}`, payload).then((r) => r.data),
  formazioneDelete: (id) => client.delete(`/formazione/items/${id}`).then((r) => r.data),
  formazioneEcmStats: (params = {}) => client.get("/formazione/ecm-stats", { params }).then((r) => r.data),

  // ─── Task B · Assicurazioni ─────────────────────────
  insuranceList: (params = {}) => client.get("/insurance/professional", { params }).then((r) => r.data),
  insuranceCreate: (payload) => client.post("/insurance/professional", payload).then((r) => r.data),
  insurancePatch: (id, payload) => client.patch(`/insurance/professional/${id}`, payload).then((r) => r.data),
  insuranceDelete: (id) => client.delete(`/insurance/professional/${id}`).then((r) => r.data),
  insuranceUpcoming: (params = {}) => client.get("/insurance/upcoming-renewals", { params }).then((r) => r.data),

  // ─── Task C2 · GDPR (Privacy & Dati) ─────────────────
  gdprListExports: () => client.get("/gdpr/exports").then((r) => r.data),
  gdprGetExport: (id) => client.get(`/gdpr/exports/${id}`).then((r) => r.data),
  gdprCreateExport: (scope) =>
    client.post("/gdpr/exports", { scope }).then((r) => r.data),
  gdprDeleteExport: (id) => client.delete(`/gdpr/exports/${id}`).then((r) => r.data),
  gdprDownloadUrl: (token) => `${API}/gdpr/exports/download/${token}`,
  gdprGetDeletionStatus: () => client.get("/gdpr/delete-account/status").then((r) => r.data),
  gdprScheduleDeletion: (payload) =>
    client.post("/gdpr/delete-account", payload).then((r) => r.data),
  gdprCancelDeletion: (token) =>
    client.post("/gdpr/delete-account/cancel", { token }).then((r) => r.data),
  gdprGetNotice: () => client.get("/gdpr/notice").then((r) => r.data),
  gdprMarkNoticeSeen: () => client.post("/gdpr/notice-seen").then((r) => r.data),

  // ─── Cicli · landing summary (Task Cicli Fase 1) ─────
  cicliSummary: () => client.get("/cicli/summary").then((r) => r.data),

  // ─── Bug 18/50 · Cicli reali + Azioni dentro Cicli ─────
  listCycleActions: (cycleId, includeCompleted = false) =>
    client.get(`/tasks/${cycleId}/actions`, { params: { include_completed: includeCompleted } }).then((r) => r.data),
  createCycleAction: (cycleId, payload) =>
    client.post(`/tasks/${cycleId}/actions`, payload).then((r) => r.data),
  cycleProgress: (cycleId) =>
    client.get(`/tasks/${cycleId}/progress`).then((r) => r.data),
};
